import { Card, Typography, Box, IconButton, Button } from "@mui/material";
import { Add, Remove, HelpOutline } from "@mui/icons-material";
import { colors, alphaVar } from "../../../../../../../../ui/colors";
import { useAreaActions } from "../hooks/useAreaActions";
import { memo } from "react";
import { getAreaQuantity } from "src/redux/features/quotes/quote-create-advanced/utils/normalize";

const AreaCardBase = ({ area }) => {
  const { changeQuantity } = useAreaActions();

  const quantity = getAreaQuantity(area);

  const activeTasksCount = Array.isArray(area.tasks) ? area.tasks.filter((t) => t.active).length : 0;

  const handleDecrease = (e) => {
    e.stopPropagation();
    if (quantity === 0) return;
    changeQuantity(area.uid, -1);
  };

  const handleIncrease = (e) => {
    e.stopPropagation();
    changeQuantity(area.uid, +1);
  };

  return (
    <Card
      sx={{
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        borderRadius: 2,
        border: `1px solid ${alphaVar(colors.gray[100], 0.8)}`,
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        transition: "all 0.2s ease",
        overflow: "visible",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 4px 8px ${alphaVar(colors.orange.base, 0.2)}`,
          borderColor: alphaVar(colors.orange.base, 0.3),
        },
        height: 200,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        position: "relative",
        p: 2,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 6,
          right: 6,
          zIndex: 99999999,
          "&:hover .help-overlay": {
            opacity: 1,
            pointerEvents: "auto",
            transform: "translateY(0)",
          },
        }}
      >
        <IconButton
          sx={{
            color: colors.gray[400],
            "&:hover": { color: colors.orange.base },
            zIndex: 999999999,
          }}
        >
          <HelpOutline fontSize="small" />
        </IconButton>

        <Box
          className="help-overlay"
          sx={{
            position: "absolute",
            top: 32,
            right: 0,
            width: 215,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            borderRadius: 2,
            p: 2,
            boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
            opacity: 0,
            transform: "translateY(-4px)",
            transition: "opacity 0.18s ease-out, transform 0.18s ease-out",
            pointerEvents: "none",
            zIndex: 999999999,
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Placeholder help text</Typography>

          <Typography sx={{ fontSize: 12, color: colors.gray[500], lineHeight: 1.4 }}>Aquí irá la explicación real del área</Typography>
        </Box>
      </Box>

      <Box
        component="img"
        src={area?.imageUrl}
        alt={area.name}
        sx={{
          width: 100,
          height: 80,
          objectFit: "contain",
        }}
      />

      {/* Nombre */}
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{
          color: colors.gray[500],
          fontSize: 14,
          textAlign: "center",
        }}
      >
        {area.name}
      </Typography>

      <Typography
        sx={{
          fontSize: 12,
          color: activeTasksCount > 0 ? colors.success[600] : colors.gray[400],
          mb: 1,
          fontWeight: 600,
        }}
      >
        {activeTasksCount} task{activeTasksCount !== 1 ? "s" : ""} attached
      </Typography>

      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
        <Button
          onClick={handleDecrease}
          disabled={quantity === 0}
          sx={{
            minWidth: 36,
            height: 32,
            borderRadius: 1.5,
            color: quantity === 0 ? colors.gray[300] : colors.orange.base,
            border: `1px solid ${alphaVar(colors.orange.base, 0.3)}`,
            "&:hover": { backgroundColor: alphaVar(colors.orange[100], 0.1) },
          }}
        >
          <Remove fontSize="small" />
        </Button>

        <Typography
          sx={{
            width: 30,
            textAlign: "center",
            fontWeight: 600,
            color: colors.gray[500],
          }}
        >
          {quantity}
        </Typography>

        <Button
          onClick={handleIncrease}
          sx={{
            minWidth: 36,
            height: 32,
            borderRadius: 1.5,
            color: colors.orange.base,
            border: `1px solid ${alphaVar(colors.orange.base, 0.3)}`,
            "&:hover": { backgroundColor: alphaVar(colors.orange[100], 0.1) },
          }}
        >
          <Add fontSize="small" />
        </Button>
      </Box>
    </Card>
  );
};

export const AreaCard = memo(AreaCardBase, (prev, next) => {
  const a = prev.area;
  const b = next.area;

  return (
    a.uid === b.uid &&
    getAreaQuantity(a) === getAreaQuantity(b) &&
    a.tasks?.length === b.tasks?.length &&
    a.tasks?.every((task, index) => task.active === b.tasks?.[index]?.active)
  );
});
